import { Component, computed, effect, inject, OnDestroy, OnInit, output, signal, } from '@angular/core';
import { ZendraLocalizationServices } from '../enums/abpLocalizationServices';
import { Confirmation, ModalComponent, ToasterService } from '@abp/ng.theme.shared';
import { GridLayout } from './services/grid-layout';
import { GridSnapshot, IGridLayout } from './interface/IGridSnapshot';
import { LayoutService } from './services/proxy/controllers';
import { AbstractControl, FormBuilder, FormGroup } from '@angular/forms';
import { finalize, Observable, Subject, takeUntil } from 'rxjs';
import {
  LayoutCreateDto, LayoutUpdateDto, SetFavoriteLayoutDto, } from './services/proxy/dtos/layout';
import { ZnMultiSelect } from '../zn-select/zn-multi-select';
import { ZnButton } from '../zn-button/zn-button';
import { IConfirmationPopupParams } from '../interfaces/IConfirmationPopupParams';
import { BaseCoreModule, LocalizationService, PermissionService } from '@abp/ng.core';
import { LAYOUT_CONFIG } from './consts/layout.config';
import { localizeWithFallback } from '../utils/localization-utils';
import { ZendraLocalizationPipe } from '../pipes/zendra-localization.pipe';

@Component({
  selector: 'app-layout-list',
  imports: [ZnButton, ZendraLocalizationPipe, BaseCoreModule, ModalComponent, ZnMultiSelect],
  templateUrl: './layout-list.html',
  styleUrl: './layout-list.scss',
})
export class LayoutList implements OnInit, OnDestroy {
  private readonly gridLayoutService = inject(GridLayout);
  private readonly layoutService = inject(LayoutService);
  private readonly localizationService = inject(LocalizationService);
  private readonly toaster = inject(ToasterService);
  private readonly fb = inject(FormBuilder);
  private readonly permissionService = inject(PermissionService);

  isVisible = signal(false);
  isVisibleChange = output<boolean>();
  isLoading = signal(false);
  isEditMode = signal(false);
  layoutLabel = signal('');
  isPersonal = signal(false);

  readonly isShared = computed(
    () => !(this.gridLayoutService.selectedLayout()?.isPersonal ?? false),
  );
  readonly permissions = computed(() => ({
    canView: this.permissionService.getGrantedPolicy(LAYOUT_CONFIG.viewPermission),
    canCreate: this.permissionService.getGrantedPolicy(LAYOUT_CONFIG.createPermission),
    canEdit: this.permissionService.getGrantedPolicy(LAYOUT_CONFIG.editPermission),
    canDelete: this.permissionService.getGrantedPolicy(LAYOUT_CONFIG.deletePermission),
    canEditBasic: this.permissionService.getGrantedPolicy(
      LAYOUT_CONFIG.editBasicLayoutPermission,
    ),
  }));

  layoutForm: FormGroup;
  private destroy$ = new Subject<void>();

  selectedLayoutChanged = output<IGridLayout>();
  favoriteLayoutChanged = output<IGridLayout>();

  localizationResource = ZendraLocalizationServices;

  constructor() {
    effect(() => {
      const layout = this.gridLayoutService.selectedLayout();
      if (layout) {
        this.layoutLabel.set(layout.name);
      }
    });
  }

  get layoutList() {
    return this.gridLayoutService.layouts();
  }

  get selectedLayout() {
    return this.gridLayoutService.selectedLayout();
  }

  get favoriteLayout() {
    return this.gridLayoutService.favoriteLayout();
  }

  get availableRoles() {
    return this.gridLayoutService.availableRoles();
  }

  get entityName() {
    return this.gridLayoutService.entityName;
  }

  get localizationKey() {
    return this.gridLayoutService.localizationKey;
  }

  ngOnInit(): void {
    this.isPersonal.set(this.isShared());
    this.initializeForm();
    this.setupFormSubscriptions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  disable() {
    return this.selectedLayout.isBasic;
  }

  private initializeForm(isBasicLayout = false, isCreatingLayout = false): void {
    let config;

    if (isCreatingLayout) {
      config = {
        layoutName: '',
        isExclusive: !this.isPersonal(),
        sharedRoles: [],
      };
    } else {
      config = isBasicLayout
        ? {
            layoutName: { value: 'TableBasicLayout', disabled: true },
            isExclusive: { value: false, disabled: true },
            sharedRoles: { value: [], disabled: true },
          }
        : {
            layoutName: { value: '', disabled: true },
            isExclusive: !this.isPersonal(),
            sharedRoles: [],
          };
    }

    this.layoutForm = this.fb.group({
      layoutName: [config.layoutName, [this.noWhiteSpaceValidator]],
      isExclusive: [config.isExclusive],
      sharedRoles: [config.sharedRoles],
    });
  }

  private setupFormSubscriptions(): void {
    this.layoutForm
      .get('isExclusive')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(isShared => {
        this.isPersonal.set(!isShared);
      });
  }

  onSelectedLayoutChanged(layout: IGridLayout): void {
    this.gridLayoutService.selectedLayout.set(layout);
    this.gridLayoutService.preAccessFiltersApplied.set([]);
    this.gridLayoutService.applySnapshot(layout.snapshot, 'layout');
    this.layoutLabel.set(layout.name);
    this.selectedLayoutChanged.emit(layout);
  }

  updateFavoriteLayout(newFavoriteLayout: IGridLayout): void {
    // if (!newFavoriteLayout || newFavoriteLayout.id <= 0) return;

    if (!newFavoriteLayout) return;

    const alreadyFavorite = this.favoriteLayout?.id === newFavoriteLayout.id;
    let config: SetFavoriteLayoutDto;

    if (alreadyFavorite && newFavoriteLayout.id <= 0) {
      this.updateFavoriteState(newFavoriteLayout, alreadyFavorite);
      return;
    }

    if (alreadyFavorite) {
      config = {
        entityName: this.entityName,
        layoutId: this.favoriteLayout.id,
        setFavorite: !alreadyFavorite,
      };
      this.updateFavoriteRequest(config, newFavoriteLayout, alreadyFavorite);
      return;
    }

    if (newFavoriteLayout.id <= 0) {
      config = {
        entityName: this.entityName,
        layoutId: this.favoriteLayout.id,
        setFavorite: false,
      };
      this.updateFavoriteRequest(config, newFavoriteLayout, alreadyFavorite);
      return;
    }

    config = config = {
      entityName: this.entityName,
      layoutId: newFavoriteLayout.id,
      setFavorite: true,
    };
    this.updateFavoriteRequest(config, newFavoriteLayout, alreadyFavorite);
  }

  private updateFavoriteRequest(
    requestConfig: SetFavoriteLayoutDto,
    newFavoriteLayout: IGridLayout,
    alreadyFavorite: boolean,
  ) {
    this.layoutService.setFavorite(requestConfig, { skipHandleError: true }).subscribe({
      next: () => {
        this.toaster.success(
          `${this.localizationResource.LanguageService}::LayoutFavoriteSaveSuccess`,
        );
        this.updateFavoriteState(newFavoriteLayout, alreadyFavorite);
      },
      error: error => this.showError(error),
    });
  }

  private updateFavoriteState(layout: IGridLayout, removeFavorite: boolean): void {
    const updatedLayouts = this.gridLayoutService.layouts().map(l => ({
      ...l,
      isFavorite: removeFavorite ? false : l.id === layout.id,
    }));

    this.gridLayoutService.layouts.set(updatedLayouts);
    this.gridLayoutService.favoriteLayout.set(removeFavorite ? null : layout);

    if (!removeFavorite) {
      this.favoriteLayoutChanged.emit(layout);
    }
  }

  checkFavoriteIcon(layoutId: number | undefined, iconClass = ''): string {
    return this.favoriteLayout?.id === layoutId
      ? `${iconClass} layout-star-icon-favorite`
      : `${iconClass} layout-star-icon-normal`;
  }

  onVisibleChange(value: boolean): void {
    this.isVisible.set(value);
    this.isVisibleChange.emit(value);
  }

  openCreateModal(): void {
    this.gridLayoutService.loadRoles();
    this.isEditMode.set(false);
    this.initializeForm(false, true);
    this.setupFormSubscriptions();
    this.layoutForm.reset({
      layoutName: '',
      isExclusive: false,
      sharedRoles: [],
    });
    this.onVisibleChange(true);
  }

  openEditModal(): void {
    const layout = this.selectedLayout;
    if (!layout) return;

    this.isEditMode.set(true);

    if (layout.isBasic) {
      this.initializeForm(true);
    } else {
      this.gridLayoutService.loadRoles();
      this.initializeForm(false);
      this.setupFormSubscriptions();
    }

    const localizedLayoutName = localizeWithFallback(this.localizationService, 
      `${this.localizationResource.LanguageService}::TableBasicLayout`,
    );
    this.layoutForm.patchValue({
      layoutName: layout.isBasic ? localizedLayoutName : layout.name,
      isExclusive: !layout.isPersonal,
      sharedRoles: layout.sharedRoles || [],
    });

    this.onVisibleChange(true);
  }

  save(): void {
    if (this.layoutForm.invalid) {
      this.layoutForm.markAllAsTouched();
      return;
    }

    if (!this.isEditMode()) {
      this.createLayout(false);
      return;
    }

    if (this.isEditMode() && this.selectedLayout.isBasic && this.selectedLayout.id < 0) {
      this.createLayout(true);
      return;
    }

    if (this.isEditMode() && this.selectedLayout.id > 0) {
      this.updateLayout();
      return;
    }
  }

  private createLayout(isBasicLayout: boolean = false): void {
    const formValue = this.layoutForm.getRawValue();
    const snapshot = this.gridLayoutService.captureSnapshot(isBasicLayout);

    const newLayoutData: LayoutCreateDto = {
      entityName: this.entityName,
      name: formValue.layoutName,
      definition: JSON.stringify(snapshot),
      isShared: !!formValue.isExclusive,
      sharedRoles: formValue.isExclusive ? formValue.sharedRoles || [] : [],
      isBasic: isBasicLayout,
    };

    this.executeLayoutOperation(
      this.layoutService.create(newLayoutData, { skipHandleError: true }),
      response => this.handleCreateSuccess(response, snapshot, isBasicLayout),
      `${this.localizationResource.LanguageService}::LayoutSaveSuccess`,
    );
  }

  private updateLayout(): void {
    const layout = this.selectedLayout;
    if (!layout) return;

    const formValue = this.layoutForm.getRawValue();
    const currentSnapshot = this.gridLayoutService.captureSnapshot();

    const updatedLayoutData: LayoutUpdateDto = {
      entityName: this.entityName,
      name: layout.isBasic ? 'TableBasicLayout' : formValue.layoutName,
      definition: JSON.stringify(currentSnapshot),
      isShared: !!formValue.isExclusive,
      sharedRoles: formValue.isExclusive ? formValue.sharedRoles || [] : [],
      concurrencyStamp: layout.concurrencyStamp,
    };

    if (this.selectedLayout.isBasic) {
      this.updateBasicLayout(updatedLayoutData, formValue, currentSnapshot);
    } else {
      this.executeLayoutOperation(
        this.layoutService.update(layout.id, updatedLayoutData, {
          skipHandleError: true,
        }),
        response =>
          this.handleUpdateSuccess(response, formValue, currentSnapshot, layout),
        `${this.localizationResource.LanguageService}::LayoutSaveSuccess`,
      );
    }
  }

  private updateBasicLayout(data: LayoutUpdateDto, formValue, snapshot: GridSnapshot) {
    // Update form to save the localization variable for basic layout as name
    const localizedLayoutName = formValue.layoutName;

    this.layoutService
      .update(this.selectedLayout.id, data, { skipHandleError: true })
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: response => {
          this.onVisibleChange(false);
          this.handleUpdateSuccess(response, formValue, snapshot, this.selectedLayout);
          this.toaster.success(
            `${this.localizationResource.LanguageService}::LayoutSaveSuccess`,
            '',
            { life: 5000, messageLocalizationParams: [localizedLayoutName] },
          );
        },
        error: error => this.showError(error),
      });
  }

  private executeLayoutOperation(
    operation$: Observable<any>,
    onSuccess: (response: any) => void,
    successMessage: string,
  ): void {
    this.isLoading.set(true);

    operation$.pipe(finalize(() => this.isLoading.set(false))).subscribe({
      next: response => {
        this.toaster.success(successMessage, '', {
          life: 5000,
          messageLocalizationParams: [response.name],
        });
        onSuccess(response);
        this.onVisibleChange(false);
      },
      error: error => this.showError(error),
    });
  }

  private handleCreateSuccess(
    response: any,
    snapshot: GridSnapshot,
    isBasicLayout: boolean,
  ): void {
    const newLayout: IGridLayout = {
      id: response.id,
      name: response.name,
      isBasic: isBasicLayout,
      isFavorite: response.isFavorite,
      isPersonal: isBasicLayout ? false : response.sharedRoles?.length === 0,
      sharedRoles: response.sharedRoles || [],
      snapshot: snapshot,
      concurrencyStamp: response.concurrencyStamp,
    };

    this.gridLayoutService.layouts.update(layouts => [...layouts, newLayout]);
    this.gridLayoutService.selectedLayout.set(newLayout);
    this.layoutLabel.set(newLayout.name);
    this.selectedLayoutChanged.emit(newLayout);
  }

  private handleUpdateSuccess(
    response: any,
    formValue: any,
    snapshot: GridSnapshot,
    originalLayout: IGridLayout,
  ): void {
    const updatedLayout: IGridLayout = {
      ...originalLayout,
      name: formValue.layoutName,
      isPersonal: !formValue.isExclusive,
      sharedRoles: formValue.isExclusive ? formValue.sharedRoles || [] : [],
      snapshot: snapshot,
      concurrencyStamp: response.concurrencyStamp,
    };

    this.gridLayoutService.layouts.update(layouts =>
      layouts.map(l => (l.id === originalLayout.id ? updatedLayout : l)),
    );

    this.gridLayoutService.selectedLayout.set(updatedLayout);
    this.layoutLabel.set(updatedLayout.name);
    this.selectedLayoutChanged.emit(updatedLayout);
  }

  deleteLayout(): void {
    const layout = this.selectedLayout;
    if (!layout) return;

    const options: IConfirmationPopupParams = {
      message: localizeWithFallback(this.localizationService, 
        `${this.localizationResource.LanguageService}::LayoutConfirmationDelete`,
      ),
      title: localizeWithFallback(this.localizationService, 
        `${this.localizationResource.LanguageService}::LayoutConfirmationDeleteTitle`,
        layout.name,
      ),
      severity: 'error',
      options: {
        hideCancelBtn: false,
        yesText: localizeWithFallback(this.localizationService, 
          `${this.localizationResource.LanguageService}::DeleteRecord`,
        ),
        cancelText: this.localizationResource.LanguageService + '::Close',
        iconTemplate: '<i class="bi bi-exclamation-octagon-fill"></i>',
      },
    };

    this.gridLayoutService.confirmationPopup(options).subscribe(status => {
      if (status === Confirmation.Status.confirm) {
        this.performDelete(layout);
      }
    });
  }

  private performDelete(layout: IGridLayout): void {
    this.isLoading.set(true);

    this.layoutService
      .delete(layout.id, { skipHandleError: true })
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: () => {
          const successMessage = `${this.localizationResource.LanguageService}::LayoutDeleteSuccess`;
          this.toaster.success(successMessage, null, {
            life: 5000,
            messageLocalizationParams: [this.selectedLayout.name],
          });

          const updatedLayouts = this.gridLayoutService
            .layouts()
            .filter(l => l.id !== layout.id);
          this.gridLayoutService.layouts.set(updatedLayouts);

          const wasFavorite = this.favoriteLayout?.id === layout.id;
          if (wasFavorite) {
            this.gridLayoutService.favoriteLayout.set(null);
          }

          const nextLayout = this.favoriteLayout ?? updatedLayouts.find(l => l.isBasic);
          if (nextLayout) {
            this.onSelectedLayoutChanged(nextLayout);
          }
        },
        error: error => this.showError(error),
      });
  }

  private showError(error: any): void {
    const message = error.error?.error?.message ?? 'Error desconocido';
    this.toaster.error(message, null, { life: 5000 });
  }

  private noWhiteSpaceValidator(
    control: AbstractControl,
  ): { [key: string]: boolean } | null {
    if (!control.value) return { required: true };
    return control.value.trim().length === 0 ? { required: true } : null;
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.layoutForm.get(fieldName);
    return !!field && field.invalid && (field.dirty || field.touched);
  }

  getFieldError(fieldName: string): string {
    const field = this.layoutForm.get(fieldName);
    if (!field?.errors) return '';

    return field.errors['required'] || field.errors['whitespace']
      ? 'DefaultErrorRequired'
      : '';
  }
}
